import { Store } from '@ngrx/store';
import { Viewport } from 'pixi-viewport';
import { ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { ClusterCenter } from 'messages/websocket.messages';

import { SceneService } from '../services/scene.service';

import { Player } from './player';
import { SocialGroup } from './social-group';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import SoundControl from '../sound-control';
import { DEFAULT_IN_GROUP_MUSIC } from '../constants/volume';

type ClusterId = number;

export const MAX_HARDCAP_SIZE = 20;
export const DEFAULT_GROUP_SIZE = 10;

export class GroupManager extends Loggable {
  // Identifies the current group for the player
  private playerGroup = new Map<string, ClusterId>();
  private groups = new Map<ClusterId, SocialGroup>();
  private delayViewportShift = true;

  private userId?: string;
  private destroyed$ = new ReplaySubject<boolean>(1);

  // New Group size cap functionality
  private groupSoftCap = false;

  // Special Access Groups
  private specialAccessGroups = false;
  private mingleMode2 = false;
  private soundControl = SoundControl.getInstance(this.sceneService);

  public constructor(
    private sceneService: SceneService,
    store: Store<AppState>,
    private _snackBar?: MatSnackBar,
  ) {
    super();
    this.setDisplayName('GroupManager');
    store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.userId = user?.userId));

    getFeatureFlagObservable(store, LaunchDarklyFlags.group_soft_cap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupSoftCap) => {
        this.groupSoftCap = groupSoftCap;
      });

    getFeatureFlagObservable(store, LaunchDarklyFlags.special_access_groups)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((specialAccessGroups) => {
        this.specialAccessGroups = specialAccessGroups;
      });

    getFeatureFlagObservable(store, LaunchDarklyFlags.mingle_mode_2)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mingleMode2) => (this.mingleMode2 = mingleMode2));
  }

  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public moveGroupCenter(viewport?: Viewport, player?: Player): void {
    if (!viewport || !player) {
      return;
    }
    let x = 0;
    let y = 0;

    let time = 120;
    const bounds = viewport.getVisibleBounds();
    const prevX = bounds.x + bounds.width / 2;
    const prevY = bounds.y + bounds.height / 2;
    const { groupId } = player.playerGroupInfo ?? {};
    const group = this.getGroup(groupId ?? 0);

    if (player.isInGroup && !player.isMoving) {
      if (group) {
        //set viewport to center of group
        x = group.x;
        y = group.y;

        if ((x !== prevX || y !== prevY) && this.delayViewportShift) {
          setTimeout(() => {
            this.delayViewportShift = false;
          }, 1000 * 2);
        }

        if (x === prevX && y === prevY) {
          this.delayViewportShift = true;
        }
      }
    } else {
      //Restore view port to center of player
      x = player.position.x;
      y = player.position.y;

      time = 16;
      this.delayViewportShift = true;
    }

    const dx = x - prevX;
    const dy = y - prevY;

    if ((dx && Math.abs(dx) > 1.5) || (dy && Math.abs(dy) > 1.5)) {
      if (dx || dy) {
        x = prevX + (dx < 0 ? Math.floor(dx / time) : Math.ceil(dx / time));
        y = prevY + (dy < 0 ? Math.floor(dy / time) : Math.ceil(dy / time));
        if ((!this.delayViewportShift && player.isInGroup) || !player.isInGroup || player.isMoving) {
          viewport.moveCenter(x, y);
          this.emitGroup();
        }
      }
    }
  }

  public setGroup({
    center,
    clusterId,
    limitExceeded,
    player,
    sag,
  }: {
    clusterId: number;
    player: Player;
    limitExceeded: boolean;
    center?: { x: number; y: number };
    sag?: boolean;
  }): SocialGroup | undefined {
    const { user } = player;
    const { userId } = user;

    if (!userId) {
      this.error('setGroup: player has no userId');
      return;
    }

    const prevGroupId = this.playerGroup.get(userId);
    const prevGroup = this.getGroup(prevGroupId ?? -1);

    // If the group has not changed, avoid removing and re-adding the player to the group. We should, however,
    // update if the player is exceeding the group limit.
    if (prevGroupId === clusterId) {
      if (limitExceeded !== player.groupWarning) {
        player.groupWarning = limitExceeded;

        if (player.isGroupSag && !player.inSag) {
          player.groupWarning = true;
        }

        prevGroup?.refreshGroup();
      }
      return;
    }

    // If the player was in a group, remove them from it.
    // When the group is empty, it will be removed.
    if (prevGroupId !== undefined) {
      this.removePlayerFromGroup(prevGroup, player);
    }

    let group = this.groups.get(clusterId);
    // Create a group if one does not exist
    if (!group) {
      group = new SocialGroup(clusterId, center);
      this.groups.set(clusterId, group);
    }

    // Group Cap triggers and validation
    if (this.groupSoftCap) {
      const hardLimitExceeded = this.checkLimitExceeded(clusterId);
      const softLimitExceeded = this.checkIsInSoftCap(clusterId);
      //Show warnings if necessary
      if (softLimitExceeded && player.isPrimaryPlayer && !sag) {
        this.sceneService.showGroupNotification(
          'Group Over Capacity',
          'This group is full, but you can still squeeze in',
          'warning',
          'warning',
          6000,
          'Squeezing you in',
          "This group is beyond the host's intended size, but you can still squeeze in.",
        );
      }
      if (hardLimitExceeded && player.isPrimaryPlayer) {
        this.sceneService.showGroupNotification(
          'Maximum Group Size Exceeded',
          'Members of this group will not hear or speak to you. Please navigate to a smaller group.',
          'error',
          'cancel',
          6000,
          "Sorry, can't fit you in",
          "This group has grown too far beyond the host's intended size. Please navigate elsewhere;",
        );
      }

      // Maintain the player group association and add the player to the group
      this.playerGroup.set(userId, clusterId);
      group.add(player, limitExceeded, softLimitExceeded, hardLimitExceeded);
    } else {
      this.playerGroup.set(userId, clusterId);
      group.add(player, limitExceeded);
    }

    if (this.userId === userId && this.mingleMode2) {
      this.checkMusicVolumeChanges(clusterId);
    }

    // Emit the group membership
    if (group.isPrimaryGroup) {
      this.emitGroup();
    }
    return group;
  }

  /**
   * Set a group's center
   *
   * The group will be created if it doesn't exist
   */
  public setGroupCenter({ c: clusterId, x, y }: ClusterCenter): void {
    let group = this.getGroup(clusterId);
    if (group) {
      group.setCenter(x, y);
    } else {
      group = new SocialGroup(clusterId, { x, y });
      this.groups.set(clusterId, group);
    }
  }

  public removeAllGroups(): void {
    this.groups.clear();
  }

  public removeParticipant(player: Player): void {
    const { user } = player;
    const { userId } = user;
    if (!userId) {
      this.error('removeParticipant: player has no userId');
      return;
    }
    const groupId = this.playerGroup.get(userId);

    if (groupId === undefined) {
      return;
    }
    const group = this.getGroup(groupId);
    this.removePlayerFromGroup(group, player);
    if (group?.isPrimaryGroup) {
      this.emitGroup();
    }
  }

  public getGroup(id: number): SocialGroup | undefined {
    return this.groups.get(id);
  }

  /**
   * Dump groups to json-serializable object
   * @returns An array of group objects
   */
  public toArray(): Record<string, unknown>[] {
    return Array.from(this.groups.values()).map((group) => group.toObject());
  }

  /**
   * Remove a player from a group.
   *
   * If the group is empty, it will be removed.
   *
   * @param group The group to remove the player from
   * @param player The player to remove
   */
  private removePlayerFromGroup(group: SocialGroup | undefined, player: Player): void {
    const { user } = player;
    const { userId } = user;
    if (userId) {
      this.playerGroup.delete(userId);
    }

    if (!group) {
      return;
    }
    group.remove(player);
    if (group.size === 0) {
      this.groups.delete(group.id);
    }
  }

  private get primaryGroup(): SocialGroup | undefined {
    return Array.from(this.groups.values()).filter((group) => group.isPrimaryGroup)[0];
  }

  private emitGroup(): void {
    const primaryGroup = this.primaryGroup;
    if (primaryGroup) {
      this.sceneService.primaryGroupMemberSubject.next(primaryGroup.membersArray);
      return;
    }
    this.sceneService.primaryGroupMemberSubject.next([]);
  }

  public zoomChanged(): void {
    this.emitGroup();
  }

  private getUsersInGroup(clusterId: number, playerGroup: Map<string, ClusterId>): string[] {
    const users = [];
    for (const [userId, groupId] of playerGroup.entries()) {
      if (groupId === clusterId) {
        users.push(userId);
      }
    }
    return users;
  }

  private getAllowedUsersToAdd(currentGroupSize: number, maxGroupSize: number): number {
    const extendedLimit = Math.min(MAX_HARDCAP_SIZE, maxGroupSize);
    return Math.max(0, extendedLimit - currentGroupSize);
  }

  public checkMusicVolumeChanges(groupId: number): void {
    const actualGroupSize = this.getUsersInGroup(groupId, this.playerGroup).length;
    //If user entered a group with more than 1 member, reduce music volume
    if (actualGroupSize > 1) {
      if (!this.soundControl.musicAutoReducedForGroup) {
        this.soundControl.reduceMusicVolume(DEFAULT_IN_GROUP_MUSIC, 'fade', true);
      }
    } else {
      // Restore music volume if the main player is leaving a group (or if the group is empty)
      this.soundControl.restoreMusicVolumeAfterGroup();
    }
  }

  private isInSoftCap(currentGroupSize: number, maxGroupSize: number): boolean {
    const extendedLimit = Math.min(MAX_HARDCAP_SIZE, maxGroupSize);
    if (currentGroupSize > extendedLimit) {
      return false;
    }
    return currentGroupSize >= Math.floor(maxGroupSize / 1.5);
  }

  public checkIsInSoftCap(groupId: number): boolean {
    const maxGroupSize = this.sceneService.maxGroupSize || MAX_HARDCAP_SIZE;
    const actualGroupSize = this.getUsersInGroup(groupId, this.playerGroup).length;
    return this.isInSoftCap(actualGroupSize, maxGroupSize);
  }

  public checkLimitExceeded(groupId: number): boolean {
    const maxGroupSize = this.sceneService.maxGroupSize || MAX_HARDCAP_SIZE;
    const actualGroupSize = this.getUsersInGroup(groupId, this.playerGroup).length;
    const usersAllowed = this.getAllowedUsersToAdd(actualGroupSize, maxGroupSize);
    return usersAllowed <= 0;
  }

  public static getConfiguredGroupSize(groupSoftCap: boolean, settingGroupSize?: number): number {
    if (groupSoftCap) {
      return settingGroupSize ? Math.max(2, Math.floor(settingGroupSize / 1.5) as number) : DEFAULT_GROUP_SIZE;
    } else {
      return settingGroupSize ? settingGroupSize : DEFAULT_GROUP_SIZE;
    }
  }

  public static getAdjustedGroupSize(groupSoftCap: boolean, toAdjustGroupSize: number): number {
    let groupSizeAdjusted = toAdjustGroupSize;
    if (groupSoftCap) {
      groupSizeAdjusted = Math.ceil(toAdjustGroupSize * 1.5);
    }
    return groupSizeAdjusted;
  }
}
